import {
	Box,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	Typography,
	Divider,
	Chip
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { GridColDef } from "@mui/x-data-grid";
import React, { Dispatch, FC, SetStateAction, useEffect, useMemo } from "react";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";

import { ColumnVisibilityType } from ".";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useUpdateUserSettingsMutation } from "../../redux/reducers/eventLogs.reducer";
import { showFeedbackNotification } from "../../redux/reducers/feedbackNotification.reducer";

interface ITableSettingsDialogProps {
	open: boolean;
	columns: GridColDef[];
	columnVisibility: ColumnVisibilityType;
	onClose: (success?: boolean) => void;
	setColumnVisibility: Dispatch<SetStateAction<ColumnVisibilityType>>;
}

const TableSettingsDialog: FC<ITableSettingsDialogProps> = (props) => {
	const { open, columns, columnVisibility, onClose, setColumnVisibility } = props;

	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const SelectAllState = useMemo<"none" | "all" | "partial">(() => {
		const values = Object.values(columnVisibility).filter((item) => !!item.checked);

		if (values.length === 0) return "none";

		if (values.length === Object.keys(columnVisibility).length) return "all";

		return "partial";
	}, [columnVisibility]);

	const selectedColumnsCount = useMemo(() => {
		return Object.values(columnVisibility).filter((item) => item.visible && item.checked).length;
	}, [columnVisibility]);

	const columnsList = useMemo(() => {
		return columns.filter(
			(columnItem) => columnVisibility[columnItem.field] && columnVisibility[columnItem.field].visible
		);
	}, [columnVisibility, columns]);

	// APIS
	const [
		updateUserSettings,
		{ isLoading: updateUserSettingsLoading, error: updateUserSettingsError, isSuccess: updateUserSettingsSuccess }
	] = useUpdateUserSettingsMutation();

	function handleChangeColumnVisibility(column: string, checked: boolean) {
		setColumnVisibility((currentColumnVisibility) => ({
			...currentColumnVisibility,
			[column]: {
				...currentColumnVisibility[column],
				checked: checked
			}
		}));
	}

	function handleChangeAllColumnsVisibility(checked: boolean) {
		setColumnVisibility((currentColumnVisibility) => {
			const updatedColumnVisibility = { ...currentColumnVisibility };

			for (const key of Object.keys(updatedColumnVisibility)) {
				if (!updatedColumnVisibility[key].disabled) {
					updatedColumnVisibility[key].checked = checked;
				}
			}

			return updatedColumnVisibility;
		});
	}

	function handleSaveButtonClick() {
		const requestData: { [columnName: string]: boolean } = {};

		for (const [columnName, visibilityDetails] of Object.entries(columnVisibility)) {
			requestData[columnName] = visibilityDetails.checked;
		}

		updateUserSettings({
			username: authState.user.username,
			data: {
				user_settings: {
					columns: requestData
				}
			}
		});
	}

	useEffect(() => {
		if (updateUserSettingsSuccess) {
			onClose(true);

			dispatch(
				showFeedbackNotification({
					message: "Table settings saved successfully",
					severity: "success"
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateUserSettingsSuccess]);

	return (
		<Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#5b4ebf",
					color: "white",
					position: "relative",
					padding: "12px 16px 8px 16px",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<SettingsIcon sx={{ fontSize: "1.3rem" }} />
					<Typography variant="h6" sx={{ fontWeight: 600 }}>
						Table Settings
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: 8, right: 8 }}>
					<IconButton
						onClick={() => onClose()}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: "4px",
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: "1.2rem" }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					padding: "16px !important",
					background: "#ffffff"
				}}
			>
				<Box
					sx={{
						marginBottom: 1,
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						alignItems: { md: "center" },
						justifyContent: "space-between"
					}}
				>
					<Typography variant="h6" sx={{ fontWeight: 600, color: "#3D3D3D", marginBottom: 0.5 }}>
						Select Columns to Display
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Chip
							label={`${selectedColumnsCount} of ${columnsList.length} columns selected`}
							color="primary"
							variant="outlined"
							size="small"
							sx={{
								borderRadius: "6px",
								fontWeight: 500,
								borderColor: "rgba(91, 78, 191, 0.3)",
								color: "#5b4ebf"
							}}
						/>
					</Box>
				</Box>

				<Divider sx={{ marginBottom: 1, borderColor: "rgba(0, 0, 0, 0.08)" }} />

				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Box
							sx={{
								padding: "8px 12px",
								background: "rgba(91, 78, 191, 0.08)",
								borderRadius: "6px",
								border: "1px solid rgba(91, 78, 191, 0.1)",
								marginBottom: 1,
								transition: "all 0.2s ease-in-out",
								"&:hover": {
									boxShadow: "0px 2px 8px rgba(91, 78, 191, 0.15)"
								}
							}}
						>
							<FormControlLabel
								label={
									<Typography sx={{ fontWeight: 600, color: "#5b4ebf", fontSize: "0.9rem" }}>
										Select All Columns
									</Typography>
								}
								control={
									<Checkbox
										checked={SelectAllState === "all"}
										indeterminate={SelectAllState === "partial"}
										onChange={(_event, checked) => handleChangeAllColumnsVisibility(checked)}
										sx={{
											color: "#5b4ebf",
											"&.Mui-checked": {
												color: "#5b4ebf"
											}
										}}
									/>
								}
							/>
						</Box>
					</Grid>

					{columnsList.map((columnItem) => (
						<Grid item xs={6} sm={6} md={4} key={columnItem.field}>
							<FormControlLabel
								label={
									<Typography
										sx={{
											fontWeight: 500,
											color: "#3D3D3D",
											fontSize: "0.8rem"
										}}
									>
										{columnItem.headerName || columnItem.field}
									</Typography>
								}
								disabled={columnVisibility[columnItem.field] && columnVisibility[columnItem.field].disabled}
								control={
									<Checkbox
										checked={columnVisibility[columnItem.field] && columnVisibility[columnItem.field].checked}
										onChange={(_event, checked) => handleChangeColumnVisibility(columnItem.field, checked)}
										sx={{
											color: "#5b4ebf",
											"&.Mui-checked": {
												color: "#5b4ebf"
											},
											"&.Mui-disabled": {
												color: "rgba(0, 0, 0, 0.26)"
											}
										}}
									/>
								}
								sx={{
									padding: "6px 8px",
									borderRadius: "6px",
									background: "rgba(255, 255, 255, 0.8)",
									border: "1px solid rgba(0, 0, 0, 0.04)",
									transition: "all 0.2s ease-in-out",
									margin: 0,
									width: "100%",
									cursor: "pointer",
									"&:hover": {
										background: "rgba(91, 78, 191, 0.04)",
										transform: "translateX(2px)",
										boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
										borderColor: "rgba(91, 78, 191, 0.2)"
									}
								}}
							/>
						</Grid>
					))}

					{updateUserSettingsError ? (
						<Grid item xs={12}>
							<Box
								sx={{
									padding: "8px 12px",
									background: "rgba(211, 47, 47, 0.08)",
									borderRadius: "6px",
									border: "1px solid rgba(211, 47, 47, 0.1)",
									marginTop: 1
								}}
							>
								<Typography variant="body2" color="#d32f2f" textAlign="center" sx={{ fontWeight: 500 }}>
									{"data" in updateUserSettingsError && updateUserSettingsError.data
										? `${String(updateUserSettingsError.data).charAt(0).toUpperCase()}${String(
												updateUserSettingsError.data
										  ).slice(1)}`
										: "An error occurred while saving settings"}
								</Typography>
							</Box>
						</Grid>
					) : null}

					<Grid item xs={12}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								marginTop: 1
							}}
						>
							<LoadingButton
								disableElevation
								variant="contained"
								color="primary"
								startIcon={<SaveIcon />}
								loading={updateUserSettingsLoading}
								loadingPosition="start"
								onClick={handleSaveButtonClick}
								sx={{
									background: "#5b4ebf",
									borderRadius: "6px",
									padding: "6px 20px",
									fontWeight: 600,
									minWidth: "120px",
									"&:hover": {
										background: "#403685",
										boxShadow: "0px 4px 12px rgba(91, 78, 191, 0.3)",
										transform: "translateY(-1px)"
									},
									transition: "all 0.2s ease-in-out"
								}}
							>
								Save Changes
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default TableSettingsDialog;
